import { useFeatureFlag, useFeatureFlagsContext } from '@vaptmenu/feature-flags-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useTenantEntitlements } from '@/features/billing/hooks/useTenantEntitlements';
import { TENANT_FEATURE_KEYS } from '@/features/billing/types/billing.types';
import { useCustomerAgentAvailabilityStore } from './useCustomerAgentAvailability';

/** Flag por estabelecimento do piloto do Atendente IA (rollout e kill switch, nunca direito). Nasce desligada. */
export const WHATSAPP_ATTENDANT_FLAG = 'panel.whatsapp-attendant';

/** Quem vê a aba, a faixa e o status (`GET /whatsapp/status`). */
export const CUSTOMER_AGENT_READ_PERMISSION = 'settings.read';

/** Conectar, desconectar e apagar dados de contato: só o dono, e só no próximo token depois do deploy. */
export const WHATSAPP_MANAGE_PERMISSION = 'whatsapp.manage';

export interface CustomerAgentAvailability {
  /**
   * O Atendente IA aparece para este usuário: a flag do piloto está ligada
   * para a loja, o plano dá `whatsapp_attendant`, ele tem a permissão pedida
   * e o servidor não recusou com 404 `whatsapp.disabled`.
   */
  available: boolean;
  /**
   * Ainda não dá para dizer que não está disponível: as flags ou os direitos
   * estão carregando. Quem decide uma navegação (a rota direta da aba e do
   * assistente) espera isto cair antes de voltar para Configurações.
   */
  pending: boolean;
}

/**
 * As flags ainda não chegaram? O provedor só existe com a sessão aberta e a
 * loja escolhida; fora dele (e nos testes que simulam só `useFeatureFlag`)
 * não há o que esperar. `useFeatureFlagsContext` lança sem provedor, e o
 * `useContext` dentro dele roda antes do lançamento, então a ordem dos hooks
 * é a mesma em toda renderização.
 */
function useFlagsLoading(): boolean {
  try {
    return useFeatureFlagsContext().isLoading;
  } catch {
    return false;
  }
}

/**
 * O Atendente IA aparece para este usuário? (change whatsapp-channel, D30)
 *
 * Quatro termos, os quatro precisam valer (ADR 0021: flag é rollout, direito é
 * o que foi pago, e o Atendente IA exige os dois):
 * 1. a flag `panel.whatsapp-attendant` da loja;
 * 2. o direito `whatsapp_attendant` em `GET /billing/entitlements`, nunca o
 *    nome do plano — trial e Essencial não veem aba, faixa nem item;
 * 3. a permissão de quem pergunta (padrão `settings.read`);
 * 4. nenhuma recusa 404 `whatsapp.disabled` lembrada para a loja.
 *
 * Os direitos só são lidos com a flag e a permissão: a loja fora do piloto
 * não faz a requisição. Molde de `usePrintingAvailable`.
 */
export function useCustomerAgentAvailable(
  permission: string = CUSTOMER_AGENT_READ_PERMISSION,
): CustomerAgentAvailability {
  const flagEnabled = useFeatureFlag(WHATSAPP_ATTENDANT_FLAG);
  const flagsLoading = useFlagsLoading();
  const permissions = useAuthStore((state) => state.user?.permissions);
  const tenantId = useAuthStore((state) => state.selectedTenant?.id ?? '');
  const disabledTenantId = useCustomerAgentAvailabilityStore((state) => state.disabledTenantId);

  const hasPermission = permissions?.includes(permission) ?? false;
  const refusedByServer = !!tenantId && disabledTenantId === tenantId;
  const readsEntitlements = flagEnabled && hasPermission && !refusedByServer;

  const { hasFeature, isLoading } = useTenantEntitlements({ enabled: readsEntitlements });
  const entitled = hasFeature(TENANT_FEATURE_KEYS.whatsappAttendant);

  return {
    available: readsEntitlements && entitled,
    pending: (hasPermission && !refusedByServer && flagsLoading) || (readsEntitlements && isLoading),
  };
}

/** O usuário tem `whatsapp.manage`. Sem ela (gerente, ou o dono com token anterior ao deploy), nada de conectar. */
export function useCanManageWhatsapp(): boolean {
  return useAuthStore((state) => state.user?.permissions.includes(WHATSAPP_MANAGE_PERMISSION) ?? false);
}
